/**
 * Builder Helper Utilities
 * 
 * Provides schema-derived data structures for the Canvas Editor builder UI.
 * These helpers extract section type metadata, default props, and slot definitions
 * from PAGE_BUILDER_SCHEMA in a builder-consumable format.
 */

import type { PageBuilderSchema, SectionTypeDefinition, PropDefinition, SlotDefinition } from './types';

/**
 * Builder-friendly section type option.
 * Used by the Canvas Editor "Add Section" UI and section management.
 */
export interface BuilderSectionTypeOption {
  type: string;
  label: string;
  description: string;
  defaultProps: Record<string, unknown>;
  slots?: Record<string, { type: string; label: string }>;
}

/**
 * Get all available section types for the builder UI.
 * 
 * Deduplicates aliases (e.g., 'categories' and 'category-grid' both point to the same
 * SectionTypeDefinition object) by keeping only the first occurrence.
 * 
 * @param schema The PAGE_BUILDER_SCHEMA to extract types from
 * @returns Array of builder section type options, deduplicated
 */
export function getBuilderSectionTypeOptions(
  schema: PageBuilderSchema
): BuilderSectionTypeOption[] {
  const seen = new Set<SectionTypeDefinition>();
  const result: BuilderSectionTypeOption[] = [];

  // Iterate keys in insertion order; skip if same object ref (dedupe aliases)
  for (const [key, sectionDef] of Object.entries(schema.sectionTypes)) {
    if (seen.has(sectionDef)) continue; // Dedupe aliases by object identity
    seen.add(sectionDef);

    // Extract default props from prop definitions
    const defaultProps: Record<string, unknown> = {};
    for (const [propKey, propDef] of Object.entries(sectionDef.props)) {
      if ('default' in propDef && propDef.default !== undefined) {
        defaultProps[propKey] = propDef.default;
      }
    }

    // Extract slot metadata (if present)
    const slots = sectionDef.slots
      ? Object.fromEntries(
          Object.entries(sectionDef.slots).map(([slotKey, slotDef]) => [
            slotKey,
            { type: slotDef.type, label: slotDef.label },
          ])
        )
      : undefined;

    result.push({
      type: key,
      label: sectionDef.label,
      description: sectionDef.description,
      defaultProps,
      slots,
    });
  }

  return result;
}

/**
 * Get default props for a specific section type.
 * 
 * @param schema The PAGE_BUILDER_SCHEMA
 * @param type The section type key (e.g., 'text-block', 'product-grid')
 * @returns Object with default prop values (empty object if no defaults or type not found)
 */
export function getDefaultPropsForSectionType(
  schema: PageBuilderSchema,
  type: string
): Record<string, unknown> {
  const sectionDef = schema.sectionTypes[type];
  if (!sectionDef) return {};

  const defaultProps: Record<string, unknown> = {};
  for (const [key, propDef] of Object.entries(sectionDef.props)) {
    if ('default' in propDef && propDef.default !== undefined) {
      defaultProps[key] = propDef.default;
    }
  }
  return defaultProps;
}

/**
 * Get section type metadata (label, description) for display.
 * 
 * @param schema The PAGE_BUILDER_SCHEMA
 * @param type The section type key
 * @returns Section metadata or null if type not found
 */
export function getSectionTypeMeta(
  schema: PageBuilderSchema,
  type: string
): { label: string; description: string } | null {
  const sectionDef = schema.sectionTypes[type];
  if (!sectionDef) return null;

  return {
    label: sectionDef.label,
    description: sectionDef.description,
  };
}

export interface SlotEditingConfig {
  editingMode: 'inline' | 'sidebar';
  inlineEditing?: {
    autoSave: {
      onBlur: boolean;
      debounceMs?: number;
    };
    visualFeedback: {
      editingClass?: string;
      cursor?: string;
      outline?: string;
    };
  };
}

/**
 * Get slot editing configuration for a specific slot.
 *
 * @param schema The PAGE_BUILDER_SCHEMA
 * @param sectionType The section type key
 * @param slotName The slot name
 * @returns Slot editing configuration or null if not found
 */
export function getSlotEditingConfig(
  schema: PageBuilderSchema,
  sectionType: string,
  slotName: string
): SlotEditingConfig | null {
  const sectionDef = schema.sectionTypes[sectionType];
  if (!sectionDef?.slots?.[slotName]) return null;

  const slotDef = sectionDef.slots[slotName];

  // Default to inline editing for text slots, sidebar for others
  const defaultEditingMode = slotDef.type === 'text' ? 'inline' : 'sidebar';

  // Default inline editing config for text slots
  const defaultInlineEditing = slotDef.type === 'text' ? {
    autoSave: { onBlur: true },
    visualFeedback: { outline: '2px solid #3b82f6' }
  } : undefined;

  return {
    editingMode: slotDef.editingMode || defaultEditingMode,
    inlineEditing: slotDef.inlineEditing || defaultInlineEditing,
  };
}
